<template>
  <kt-modal :visible="true" size="modal-lg" title="Clock Stopped" :dismissable="false" @dismiss="onClose">
    <template #body>
      <!-- Loading state -->
      <div v-if="isLoading" class="text-center py-4">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="mt-2 text-muted">{{ loadingMessage }}</p>
      </div>

      <div v-else>
        <!-- Job / Request Summary -->
        <div class="row g-3 mb-3">
          <div class="col-md-6">
            <div class="card bg-light h-100">
              <div class="card-body">
                <div class="mb-2">
                  <small class="text-muted text-uppercase">{{ titleLabel }}</small>
                  <p class="mb-1 fw-semibold">{{ displayTitle }}</p>
                </div>
                <div class="mb-2" v-if="clientName">
                  <small class="text-muted text-uppercase">Client</small>
                  <p class="mb-1 fw-semibold">{{ clientName }}</p>
                </div>
                <div class="mb-0">
                  <small class="text-muted text-uppercase">Time Worked</small>
                  <p class="mb-0 fw-semibold">{{ timeWorkedDisplay }}</p>
                </div>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <div class="card bg-light h-100">
              <div class="card-body">
                <div class="mb-2">
                  <small class="text-muted text-uppercase">Linked Job</small>
                  <p class="mb-1 fw-semibold">{{ jobDisplay }}</p>
                </div>
                <div class="mb-0">
                  <small class="text-muted text-uppercase">Job Owner</small>
                  <p class="mb-0 fw-semibold">{{ jobOwnerDisplay }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Editable completion fields -->
        <div class="row g-3" v-if="workOrder">
          <div class="col-md-6">
            <kt-select
              label="Request Status"
              v-model="form.Status"
              :options="requestStatusOptions"
              label-key="label"
              value-key="value"
              placeholder="Choose Request Status"
              allow-zero
            />
          </div>
          <div class="col-12">
            <kt-input
              type="textarea"
              rows="2"
              label="Internal Status"
              input-class="form-control form-control-sm"
              v-model="form.InternalStatus"
              placeholder="Internal notes/status..."
            />
          </div>
          <div class="col-12">
            <kt-input
              type="textarea"
              rows="2"
              label="External Status"
              input-class="form-control form-control-sm"
              v-model="form.ExternalStatus"
              placeholder="Public status visible to the client..."
            />
            <div class="form-text text-muted">
              <i class="fa fa-info-circle me-1"></i>This field is public to the client.
            </div>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <button class="btn btn-primary" @click="onSubmit" :disabled="isSaving || isLoading">
        <span v-if="isSaving" class="spinner-border spinner-border-sm me-1" role="status"></span>
        Submit
      </button>
    </template>
  </kt-modal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import KtModal from '@/components/UI/KtModal.vue';
import KtInput from '@/components/UI/KtInput.vue';
import KtSelect from '@/components/UI/KtSelect.vue';
import ApiService from '@/services/ApiService';
import Alert from '@/services/Alert';
import Toast from '@/services/Toast';
import ClockHelper from '@/components/Clock/ClockHelper';
import { useRequestStatusStore } from '@/stores/RequestStatusStore';

const props = defineProps<{
  data: {
    task?: any;
    clock?: any;
    request?: any;
    client?: any;
  } | null;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'view-job', jobId: number): void;
  (e: 'add-followup', payload: any): void;
}>();

const ch = new ClockHelper();

// Loading / saving state
const isLoading = ref(true);
const loadingMessage = ref('Loading details...');
const isSaving = ref(false);

// Resolved entities (reloaded so we have full data incl. WorkOrder)
const task = ref<any>(null);
const workOrder = ref<any>(null);
const request = ref<any>(null);

// Which entity the Status dropdown controls: 'workorder' | 'request'
const statusTarget = ref<'workorder' | 'request'>('request');

// Hardcoded request-status options until the unified Status API is ready
const requestStatusStore = useRequestStatusStore();

// Editable form fields
const form = ref<{
  InvoiceTime: number | null;
  InvoiceAmount: number | null;
  ActualWork: string | null;
  InvoiceText: string | null;
  Status: number | null;
  InternalStatus: string | null;
  ExternalStatus: string | null;
}>({
  InvoiceTime: null,
  InvoiceAmount: null,
  ActualWork: null,
  InvoiceText: null,
  Status: null,
  InternalStatus: null,
  ExternalStatus: null,
});

const requestStatusOptions = requestStatusStore.options;

const titleLabel = computed(() => (statusTarget.value === 'workorder' ? 'Request' : 'Job'));

const displayTitle = computed(() => {
  if (statusTarget.value === 'workorder' && workOrder.value) {
    const wo = workOrder.value;
    return `#${wo.Id} - ${wo.RequestTitle || wo.Description || '(No title)'}`;
  }
  const req = request.value || props.data?.request;
  return req?.RequestDetailedDescription || req?.TaskDetail || '(No description)';
});

const clientName = computed(() => props.data?.client?.Company || '');

const jobDisplay = computed(() => {
  const req = request.value || props.data?.request;
  if (!req?.Id) return '-';
  const desc = req.RequestDetailedDescription || req.TaskDetail || '(No description)';
  return `#${req.Id} - ${desc}`;
});

const jobOwnerDisplay = computed(() => {
  const owner = request.value?.Owner || props.data?.request?.Owner;
  return owner?.TechnicianName || owner?.Name || '-';
});

const timeWorkedDisplay = computed(() => {
  const total = props.data?.clock?.TimeTotal;
  if (total && Number(total) > 0) {
    return ch.toHHMMSS(Number(total));
  }
  const hrs = task.value?.ActualTime ?? props.data?.task?.ActualTime;
  return hrs != null ? `${hrs} hrs` : '-';
});

const viewJobId = computed(() => request.value?.Id || props.data?.request?.Id || props.data?.task?.RequestId || null);

onMounted(async () => {
  await loadDetails();
});

const loadDetails = async () => {
  isLoading.value = true;
  try {
    const initialTask = props.data?.task;
    const initialRequest = props.data?.request;

    // Reload the task so we get the full record (incl. WorkOrder/Request navigation)
    if (initialTask?.Id) {
      try {
        task.value = await ApiService.loadTime(initialTask.Id);
      } catch (e) {
        console.warn('ClockDoneModal: loadTime failed, falling back to provided task', e);
        task.value = initialTask;
      }
    } else {
      task.value = initialTask;
    }

    // Preserve relationship fields that the full load may not include
    if (task.value) {
      task.value.InitialRequest = task.value?.InitialRequest ?? initialTask?.InitialRequest;
      task.value.WorkOrderId = task.value?.WorkOrderId ?? initialTask?.WorkOrderId;
    }

    request.value = task.value?.Request || initialRequest;

    // Pre-fill editable fields from the resolved task
    form.value.InvoiceTime = task.value?.InvoiceTime ?? null;
    form.value.InvoiceAmount = task.value?.InvoiceAmount ?? null;
    form.value.ActualWork = task.value?.ActualWork ?? null;
    form.value.InvoiceText = task.value?.InvoiceText ?? null;

    // Resolve the WorkOrder(Title='Request') ticket if one is linked.
    // InitialRequest is the canonical request-ticket column; WorkOrderId is for invoice.
    const workOrderId = task.value?.InitialRequest
      || initialTask?.InitialRequest
      || (task.value?.WorkOrder?.Title === 'Request' ? task.value?.WorkOrderId : null)
      || (initialTask?.WorkOrder?.Title === 'Request' ? initialTask?.WorkOrderId : null)
      || null;
    if (workOrderId) {
      try {
        workOrder.value = await ApiService.loadWorkOrder(workOrderId);
        statusTarget.value = 'workorder';
        form.value.Status = workOrder.value?.Status ?? null;
        form.value.InternalStatus = workOrder.value?.InternalNotes ?? null;
        form.value.ExternalStatus = workOrder.value?.ExternalStatus ?? null;
      } catch (e) {
        console.warn('ClockDoneModal: loadWorkOrder failed', e);
        statusTarget.value = 'request';
        form.value.Status = request.value?.RequestStatus ?? null;
      }
    } else {
      statusTarget.value = 'request';
      form.value.Status = request.value?.RequestStatus ?? null;
    }
  } catch (e: any) {
    console.error('ClockDoneModal: failed to load details', e);
    Alert.notify('Failed to load clock details');
  } finally {
    isLoading.value = false;
  }
};


const onSubmit = async () => {
  if (isLoading.value) return;
  isSaving.value = true;
  try {
    // Save the status/notes against the linked WorkOrder(Title='Request') ticket
    if (workOrder.value?.Id) {
      workOrder.value.Status = form.value.Status;
      workOrder.value.InternalNotes = form.value.InternalStatus;
      workOrder.value.ExternalStatus = form.value.ExternalStatus;
      await ApiService.saveWorkOrder(workOrder.value);
      Toast.success('Request status saved');
      emit('close');
    } else {
      emit('close');
    }
  } catch (e: any) {
    console.error('ClockDoneModal: submit failed', e);
    Alert.notify('Failed to save request status: ' + (e?.message || 'Unknown error'));
  } finally {
    isSaving.value = false;
  }
};

const onClose = () => {
  emit('close');
};
</script>
